/**
 * Iter-127 — Shared episode-grouping helpers.
 * Iter-129 — Sort now delegates to compareScenes for a single source
 * of truth on scene ordering across grouped + flat lists.
 *
 * Lifted out of ScenesHub.jsx so OnSetMode + PrepMode (and any future
 * scene-list surface) can render the same Episode / App accordion
 * UX as the main Scenes page. Single source of truth for:
 *
 *   * NO_EPISODE sentinel (scenes without an explicit episode)
 *   * Episode group construction (sort, scene-order-within-group)
 *   * TV mode detection (any explicit episode at all → TV)
 *   * Episode label rendering
 *
 * The grouping function preserves the OnSetFlow pilot's visual
 * ordering: numeric episodes ascending, then alpha episodes, then
 * the NO_EPISODE bucket last.
 */

import { compareScenes, compareEpisodes } from "./scenes";

export const NO_EPISODE = "__none__";
export const EPISODE_LABEL_FILM = "Scenes";
// Iter-153 — shorthand "EP 1" is the canonical production language
// across the app. Used by scene group headings, episode filters,
// cast review chips, etc.
export const EPISODE_LABEL_TV = (ep) => `EP ${ep}`;

/**
 * Group an array of scenes by `episode`.
 * Returns: [{ key: episodeStr | NO_EPISODE, scenes: [...] }, ...]
 *
 * @param {Array} scenes  Scene rows as returned by /projects/{pid}/scenes
 */
export function groupScenesByEpisode(scenes) {
    const buckets = new Map();
    for (const s of scenes || []) {
        const key = s.episode ? String(s.episode) : NO_EPISODE;
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(s);
    }
    for (const arr of buckets.values()) {
        arr.sort(compareScenes);
    }
    const keys = [...buckets.keys()].sort((a, b) => {
        if (a === NO_EPISODE) return 1;
        if (b === NO_EPISODE) return -1;
        return compareEpisodes(a, b);
    });
    return keys.map((key) => ({ key, scenes: buckets.get(key) }));
}

/** True when at least one scene has an explicit episode. */
export function isTvProject(grouped) {
    return Array.isArray(grouped) && grouped.some((g) => g.key !== NO_EPISODE);
}

/**
 * Get the human label for a group key.
 *   key === NO_EPISODE → "Scenes" (film projects)
 *   "1" / "1A" / "Pilot" → "EP 1" / "EP 1A" / "EP Pilot"
 */
export function episodeLabelFor(key) {
    return key === NO_EPISODE ? EPISODE_LABEL_FILM : EPISODE_LABEL_TV(key);
}
